import React, { Component } from "react";
class Pegasus extends Component {
  render() {
    return (
      <section className="col-7 nested">
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            The digital age has ushered in unprecedented advancements in
            technology, revolutionising the way we communicate, work, and
            navigate our daily lives. However, this technological evolution
            comes hand in hand with intricate challenges, particularly in the
            realm of surveillance. The ubiquity of surveillance practices raises
            concerns about privacy, human rights, and the very fabric of
            democratic institutions and the aim of this paper is to highlight
            how certain corporations like Pegasus leverage this intricate
            digital space to conduct and justify surveillance.
            <br></br>
            <br></br>
            This paper delves into the institutions that play a role in
            perpetuating widespread surveillance, the concept of surveillance
            realism, as well as how surveillance realism is maintained in
            society through the ‘social imaginary’ and privacy cynicism. To
            explore the heart of this complex landscape, the paper focuses on
            the case study of Pegasus spyware, a tool developed by the
            Israel-based company NSO Group. Pegasus, ostensibly marketed for
            combating crime and terrorism, has been routinely employed by
            governments worldwide for surreptitious surveillance. The role of
            both the NSO Group and state-recognised authorities of power in
            legitimising the use of Pegasus is further examined. The revelations
            surrounding its usage, targeting political opponents, public
            officials, journalists, human rights defenders, and civil society
            actors, have sparked intense debates about the erosion of privacy
            and the potential damage to the principles of the rule of law. As we
            navigate this intricate web of digital espionage, it becomes
            imperative to understand the broader context of surveillance and its
            impact on democratic values. From secret state surveillance to the
            specific targeting of individuals, the ramifications extend beyond
            mere privacy infringements.
          </p>
        </div>

        <h2 className="large-spacer-top col-5">Surveillance</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Surveillance refers to “the focused, systematic and routine
            attention to personal details for the purposes of influence,
            management, protection or direction” (Lyon, 2007, p. 14), including
            the gathering of data by digital means, and it can be applied in
            various contexts. Surveillance methods may range from passive
            observation to active data collection (Ouagal, Hendrikx, Saegerman,
            & Berkvens, 2010). Advancements in technology and surveillance
            strategies have occurred simultaneously. These advancements have
            resulted in one influencing the other (Lyon, 2022). The potential
            perils of surveillance manifest as algorithms and analytics are
            harnessed by major corporations and governmental entities to
            spatially and temporally situate individuals and groups. This
            utilisation aims not only to comprehend historical occurrences but
            also to prognosticate future behaviours (Andrew & Baker, 2021).
          </p>
        </div>

        <h3 className="project-subheading medium-spacer-top col-5">
          <strong>Types of surveillance</strong>{" "}
        </h3>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            There are two broad types of surveillance – mass surveillance and
            targeted surveillance. Mass surveillance is undirected and passive,
            meant to gather data for future use. In contrast, targeted
            surveillance directs focus on specific individuals. Authorised
            public agencies may be involved in targeted surveillance for a
            specific investigation or operation (House of Lords - Surveillance:
            Citizens and the State - Constitution Committee, 2009, Chapter 2).
          </p>
        </div>

        <h3 className="project-subheading medium-spacer-top col-5">
          <strong>The actors involved</strong>{" "}
        </h3>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            The actors involved in surveillance are institutional structures,
            including the corporate, state, academic and media sectors.
            Government agencies, academia, and data firms form an ecosystem for
            the flow and exchange of data through personnel as well as through
            their exchange of technological innovations. Academics, having a
            vested interest in datafication and unrestrained access to people's
            data, maintain the credibility of this system. (Van Dijck, 2014). By
            shifting attention from mass surveillance to the espionage conducted
            on elites and terrorists, the news media has had a huge impact on
            normalising surveillance in the interest of national security as
            well (Hintz, Dencik, & Wahl-Jorgensen, 2017) (Wahl-Jorgensen,
            Bennett, & Taylor, 2017).
          </p>
        </div>

        <h2 className="large-spacer-top col-5">Surveillance Realism</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Tackling the relationship between privacy and disempowerment,
            (Dencik & Cable, 2017) introduced the concept of 'surveillance
            realism' to explain why the general public accepts surveillance.
            'Surveillance realism' is based on 'capitalist realism', a theory by
            Mark Fisher (2009). Fisher's theory describes how people see
            capitalism as the only viable system, despite its flaws. (Dencik &
            Cable, 2017) define surveillance realism as the belief that
            surveillance is the only choice, even though its flaws and
            unfairness are widely acknowledged. The authors emphasise that the
            contemporary form of digital surveillance is maintained by “feelings
            of widespread resignation, not consent, to the status quo” among
            digital citizens.
          </p>
        </div>

        <h3 className="project-subheading medium-spacer-top col-5">
          <strong>The Social Imaginary</strong>{" "}
        </h3>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            A school of thought that interprets the means by which surveillance
            realism persists is the role of the ‘social imaginary’. Taylor
            (2002) uses the term social imaginary to refer to the public's
            unspoken understanding of society, which inherently influences the
            creation of modern institutions. Taylor’s focus is on how regular
            people inculcate ideas such as surveillance into their daily habits,
            giving surveillance a widely shared sense of legitimacy (p. 119).
            Surveillance capitalists have shifted away from dependence on
            individuals as mere consumers. Rather, the locus of supply and
            demand directs the surveillance capitalist entity towards
            enterprises with the purpose of forecasting the conduct of
            populations, groups, and individuals (Zuboff, 2019). The idea of
            mass data collection is seen as necessary and inevitable, advanced
            through compromised equilibrium and stabilising the nature of
            digital infrastructures. These infrastructures are seen as 'common
            sense' in contemporary society. Ubiquitous data collection has
            created a social imaginary of resignation, despite feelings of
            unease and recognition of discriminatory effects. In the context of
            surveillance realism, the system's injustices and fallacies become a
            small price to pay for inefficiency, threats, and terror. (Dencik,
            2018).
          </p>
        </div>

        <h3 className="project-subheading medium-spacer-top col-5">
          <strong>Privacy Cynicism</strong>{" "}
        </h3>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Privacy apathy, privacy fatigue and privacy cynicism are
            well-researched concepts (Hargittai & Marwick (2016) (Lutz &
            Hoffmann, 2017) (Choi et al., 2018) (Lutz, Hoffmann, & Ranzini,
            2020) that provide a further window into how surveillance realism
            sets in. These concepts collectively contribute to surveillance
            realism, acknowledging that individuals, despite concerns, may
            succumb to resignation, exhaustion, and scepticism in the face of
            pervasive surveillance. The path of least resistance for digital
            citizens is to accept surveillance as a necessary evil in society.
          </p>
        </div>

        <h2 className="large-spacer-top col-5">Pegasus Spyware</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            The use of technology to survey devices has existed for a while now.
            Software that installs itself on a user's computer (often without
            permission) and then gathers and reports detailed information about
            that user is known as spyware (a portmanteau for spying software)
            (Garrie, 2005). It has significant potential for industrial
            espionage and may have legal ramifications. It can be remotely
            logged and can snoop on every action made on the end user's
            computer. (Blakley, Garrie, & Armstrong, 2005). One such spyware is
            Pegasus, a mobile phone spyware suite developed by Israel-based
            “Cyber Warfare” vendor NSO Group. In order to monitor a target, a
            specially designed exploit link is deployed. Once clicked, the link
            launches a series of zero-day exploits that break through phone
            security measures and install Pegasus without the user's awareness
            or consent. Following the phone's exploitation and installation of
            Pegasus, the device starts corresponding with the Pegasus operator's
            command and control servers in order to receive and carry out
            commands from the operators and return the target's private
            information, which includes calendar events, passwords, contact
            lists, text messages, and live voice calls. Even the phone's camera
            and microphone can be turned on by the operator to record activity
            (Marczak, Scott-Railton, McKune, Abdul Razzak, & Deibert, 2018, p.
            7). In a DNS probing study conducted by (Marczak, Scott-Railton,
            McKune, Abdul Razzak, & Deibert, 2018). 45 nations were identified
            as potential sites of surveillance activity by Pegasus operators,
            with a revelation that at least 10 countries were actively
            conducting cross-border monitoring using the spyware. The real-time
            surveillance capabilities of the Pegasus spyware are an immediate
            threat to human rights.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">
          Brief Background of Pegasus and NSO
        </h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            The academic discourse surrounding Pegasus extends beyond its
            technical capabilities, delving into legal, ethical, and human
            rights considerations. The existence of Pegasus was first identified
            in the public sphere in August 2016, after a failed attempt to
            install the programme on the iPhone of a human rights activist,
            Ahmed Mansoor, who was an internationally recognised human rights
            defender and member of Human Rights Watch’s advisory committee
            (Marczak, & Scott-Railton, 2016). This led to an investigation,
            revealing details about the spyware and its abilities in terms of
            the security vulnerabilities it exploited. NSO Group has been
            licensing and marketing it to governments all over the world.
            Governments in various countries have been exploiting Pegasus to
            surveil their own citizens, journalists, human rights activists,
            opposition party politicians, ministers, businessmen, government
            officials and scientists. Reports of deaths and sudden
            disappearances of human rights activists, diplomats, lawyers and
            journalists have come under the preview of this spyware (Richard &
            Rigaud, 2021). In their paper, (Kaster, & Ensign, 2023) provide a
            complete timeline for NSO Group technologies.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">NSO Group’s Response</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Despite garnering significant attention over the years, the NSO
            Group has maintained a position of strict silence in the wake of the
            controversy. However, on August 2020, Shalev Hulio, as NSO's CEO, in
            an interview with MIT Technology Review deflected the allegations by
            affirming that the issues lie within the industry and are not
            specific to NSO. In 2021, NSO Group suffered a fallout from negative
            press against the group from ‘The Pegasus Project’, an ongoing
            international journalist investigation on the Pegasus Spyware and
            the NSO Group, a collaboration of 17 news media organisations
            involving 80 journalists from various media companies alongside
            several human rights organisations, groups and activists (Richard &
            Rigaud, 2021). In the wake of widespread condemnation from media
            outlets, the company issued a press release titled “Enough is
            Enough!”, claiming to investigate the misuse of their technology but
            stating that they would no longer be responding to media inquiries
            on this issue (NSO Group, 2021). The NSO group has justified selling
            their software for targeted surveillance with their mission
            statement, “Help governments protect innocents from terror and crime
            by providing them with the best intelligence technology of its kind”
            (NSO group, 2021, p. 5). NSO justifies its surveillance by
            emphasising the practical and emotional reasons for its
            encryption-breaking technology, claiming to have the ability to
            safeguard a hypothetical benevolent group from security risks.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">
          Legitimisation strategies employed by NSO
        </h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Suchman (1995, p. 574) considers that “legitimacy is a generalised
            perception or assumption that the actions of an entity are
            desirable, proper, or appropriate within some socially constructed
            system of norms, values, beliefs, and definitions.” To ascertain the
            legitimisation of the use of such technology by NSO, (Kotliar &
            Carmi, 2023) have identified four legitimisation practices:
            securitisation discourse, Zionist patriotism, ethics washing and
            normalisation. These practices ensure the company's survivability
            and sustain surveillance realism. Securitisation is maintained by
            the aforementioned assurance of protection from terror or threats to
            society. Suchman (1995, p. 571) states that dynamics in the
            organisational environment do not just stem from technological
            imperatives but also from cultural norms, symbols, beliefs and
            rituals. (Kotliar & Carmi, 2023) examine how NSO overtly portrays
            its corporate identity as rooted in Zionism, subsequently
            emphasising its strategies for legitimisation, which revolve around
            three prominent cultural tropes in Israeli society: commemorating
            the Holocaust, contributing to nation-building, and portraying a
            narrative of victimisation. By culturally aligning itself with
            societal norms, NSO constructs a national identity and delineates
            the “accusations against the company as an organised, anti-Israeli,
            or even antisemitic plot” . (Kotliar & Carmi, 2023) describe NSO’s
            ethics washing practices by highlighting NSO’s insistence on their
            internal ethics, unparalleled transparency and their compliance with
            Israeli and international codes of practice. Indeed, NSO’s website
            has a ‘Governance’ page where they mention their Governance, Risk
            and Compliance Committee board, along with their vetting process for
            clients who are declared to be only government intelligence and law
            enforcement agencies (“Governance”, NSO, n.d.). Lastly, the NSO
            group’s normalisation practices involves discursive strategies,
            generally on social media, to create a self-mage as a commonplace,
            universally conventional technology enterprise, distinctly distant
            from the portrayal of a malevolent cyber-espionage entity.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">
          Role of the state in legitimisation of Surveillance
        </h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            The responsibility of governance and the practices of legitimisation
            of surveillance do not solely lie with NSO, but with states as well.
            (Alexander, & Krishna, 2022) emphasise that “only state
            participation gives the act of surveillance some sort of justifiable
            legitimacy, backed by reasons such as countering terrorist action or
            other highly criminal actions; otherwise, this cyber-surveillance is
            in itself prima facie unlawful”. Over the course of several decades,
            security agencies within governmental frameworks have demonstrated
            an increasing inclination toward and desire for digital
            surveillance. Within regimes lacking adequate checks and balances on
            their security apparatus or exhibiting characteristics of
            authoritarianism, corruption, or illiberalism, inherent abuses have
            ensued. While the industry portrays its offerings as empowering
            governments to address critical issues of crime and terrorism, the
            unrestrained deployment of their products has evolved into a
            convenient mechanism for eroding public accountability and
            suppressing political opposition, both domestically and
            internationally. (Deibert, 2022).
          </p>
        </div>
        <h2 className="large-spacer-top col-5">Implications on Human Rights</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Being targeted by the Pegasus spyware has left journalists
            distressed and paranoid (Katibah, 2023, p. 50). The impact of the
            surveillance technology includes infringement upon the right to
            privacy, freedom of expression and other human rights and
            fundamental freedoms of human rights defenders (Kaldani & Prokopets,
            2022). Scholars have questioned the legality of Pegasus,
            highlighting the need to re-examine the cyber landscape. The mere
            presence of such spyware has prompted scrutiny into its compliance
            with legal frameworks (Alexander & Krishna, 2022). The privatised
            nature of Pegasus, deemed military-grade spyware, introduces a
            dimension of privatised espionage. This has implications for privacy
            and security, emphasising the need for regulatory frameworks to
            address potential abuses (Kaster & Ensign, 2023). While the Pegasus
            spyware scandal has drawn attention, academic critiques have
            questioned the rigor of certain reports. Olivas Osuna’s (2023)
            critical review of specific reports emphasises the importance of
            adhering to academic research conventions and protocols.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">Mitigation proposals</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            (Deibert, 2022), considering the urgency of the problem, suggests
            short-term measures to penalise violators and lessen damages that
            liberal democracies could and need to immediately undertake in
            defence of the rule of law and human rights. In his view,
            governments should be more transparent and accountable when it comes
            to their own procurement of surveillance technology. They could
            tighten export controls to ensure that surveillance vendors in their
            jurisdictions comply meaningfully with UN Human Rights Principles.
            Additionally, governments could leverage current regulations to
            legally hold the worst offenders in the mercenary spyware industry
            accountable. Finally, they could create legislation to enable
            private lawsuits, empowering victims of targeted espionage to hold
            governments and companies accountable.
            <br></br>
            <br></br>
            (Alexander & Krishna, 2022), however, highlight that in the
            existence of a state-centric approach within the realm of
            international law, particularly international human rights law,
            corporations have remained exempt from robust obligations to
            safeguard individuals against their surveillance activities. The
            authors insist that international institutions need to formulate a
            shared responsibility framework, as it is crucial to avoid the
            centralization of these mechanisms within the state framework.
            Instead, there is a necessity to facilitate the dynamic engagement
            of diverse stakeholders, encompassing civil societies, state actors,
            and corporate bodies.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">Conclusion</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            In conclusion, the paper highlights the far-reaching consequences of
            surveillance and the intricate web of actors involved. The Pegasus
            spyware serves as a poignant case study, illustrating the urgency
            for regulatory frameworks to address potential abuses in the
            privatised world of espionage. The impacts on journalists and human
            rights defenders underscore the need for a re-examination of the
            cyber landscape and a critical review of academic reports to ensure
            accuracy and adherence to research conventions. As technology
            continues to advance, it becomes imperative to strike a balance
            between security measures and safeguarding fundamental human rights,
            including privacy and freedom of expression. This paper contributes
            to the ongoing discourse on surveillance, shedding light on its
            nuances, challenges, and the imperative for responsible governance
            in an increasingly digitalised world.
          </p>
        </div>
        <h2 className="large-spacer-top col-5">References</h2>
        <br></br>
        <br></br>
        <div className="text-group">
          <p className="large-paragraph medium-spacer-bottom small-spacer-top">
            Alexander, A., & Krishna, T. (2022). Pegasus project: Re-questioning
            the legality of the cyber-surveillance mechanism. Laws, 11(6), 85.
            https://doi.org/10.3390/laws11060085
            <br></br>
            <br></br>
            Andrew, J., & Baker, M. (2021). The general data protection
            regulation in the age of surveillance capitalism. Journal of
            Business Ethics, 168(3), 565-578.
            https://doi.org/10.1007/s10551-019-04239-z
            <br></br>
            <br></br>
            Blakley, A. F., Garrie, D. B., & Armstrong, M. J. (2005). Coddling
            spies: why the law doesn't adequately address computer spyware. Duke
            L. & Tech. Rev., 4, 1.
            <br></br>
            <br></br>
            Choi, H., Park, J., & Jung, Y. (2018). The role of privacy fatigue
            in online privacy behavior. Computers in Human Behavior, 81, 42-51.
            https://doi.org/10.1016/j.chb.2017.12.001
            <br></br>
            <br></br>
            Deibert, R. (2022). Protecting society from surveillance spyware.
            Issues in Science and Technology, 38(2), 15-17.
            <br></br>
            <br></br>
            Dencik, L., & Cable, J. (2017). The advent of surveillance realism:
            Public opinion and activist responses to the Snowden leaks.
            International Journal of Communication, 11, 763- 781.
            <br></br>
            <br></br>
            Dencik, L. (2018). Surveillance realism and the politics of
            imagination: Is there no alternative? Krisis (Amsterdam, Netherland
            : 2000), (1), 1-3.
            <br></br>
            <br></br>
            Donelle, L., Comer, L., Hiebert, B., Hall, J., Shelley, J. J.,
            Smith, M. J., Kothari, A., Burkell, J., Stranges, S., Cooke, T.,
            Shelley, J. M., Gilliland, J., Ngole, M., & Facca, D. (2023). Use of
            digital technologies for public health surveillance during the
            COVID-19 pandemic: A scoping review. SAGE Publications.
            https://doi.org/10.1177/20552076231173220
            <br></br>
            <br></br>
            Fisher, M. (2009). Capitalist realism: Is there no alternative?.
            Zero Books.
            <br></br>
            <br></br>
            Garrie, D. B. (2005). The legal status of software. The John
            Marshall Journal of Computer & Information Law, 23(4), 711-769.
            <br></br>
            <br></br>
            Hargittai, E., & Marwick, A. (2016). "what can I really do?"
            explaining the privacy paradox with online apathy. International
            Journal of Communication (Online), , 3737.
            <br></br>
            <br></br>
            Hintz, A., Dencik, L., & Wahl-Jorgensen, K. (2017). Digital
            citizenship and surveillance society: Introduction. International
            Journal of Communication, 11, 731-739.
            <br></br>
            <br></br>
            House of Lords, UK Parliament. Surveillance: Citizens and the State
            - Constitution Committee. (2009). Publications.parliament.uk.
            https://publications.parliament.uk/pa/ld200809/ldselect/ldconst/18/1804.htm
            <br></br>
            <br></br>
            Katibah, L. (2023). The Politics of Pegasus Spyware: Examining the
            Impact of Surveillance on Journalism.
            <br></br>
            <br></br>
            Kaldani, T., & Prokopets, Z. (2022). Pegasus Spyware and its impacts
            on human rights. Information Society Department. Council Of Europe.
            https://rm.coe.int/pegasus-spyware- report-en/1680a6f5d8
            <br></br>
            <br></br>
            Kaster, S. D., & Ensign, P. C. (2023). Privatized espionage: NSO
            group technologies and its pegasus spyware. Thunderbird
            International Business Review, 65(3), 355-364.
            https://doi.org/10.1002/tie.22321
            <br></br>
            <br></br>
            Kotliar, D. M., & Carmi, E. (2023). Keeping pegasus on the wing:
            Legitimizing cyber espionage. Information, Communication & Society,
            , 1-31. https://doi.org/10.1080/1369118X.2023.2245873
            <br></br>
            <br></br>
            Lutz, C., & Hoffmann, C. P. (2017). The dark side of online
            participation: Exploring non- , passive and negative participation.
            Information, Communication & Society, 20(6), 876-897.
            https://doi.org/10.1080/1369118X.2017.1293129
            <br></br>
            <br></br>
            Lutz, C., Hoffmann, C. P., & Ranzini, G. (2020). Data capitalism and
            the user: An exploration of privacy cynicism in Germany. New Media &
            Society, 22(7), 1168- 1187. https://doi.org/10.1177/1461444820912544
            <br></br>
            <br></br>
            Lyon, D. (2007). Surveillance studies: An overview. Polity.
            <br></br>
            <br></br>
            Lyon, D. (2022). Reflections on forty years of "surveillance
            studies". Surveillance & Society, 20(4), 353-356.
            <br></br>
            <br></br>
            Marczak, B., & Scott-Railton, J. (2016). The million dollar
            dissident: NSO group’s iPhone zero-days used against a UAE human
            rights defender. Citizen Lab, 24.
            <br></br>
            <br></br>
            Marczak, B., Scott-Railton, J., McKune, S., Abdul Razzak, B., &
            Deibert, R. (2018). Hide and seek: Tracking NSO group’s Pegasus
            spyware to operations in 45 countries.
            <br></br>
            <br></br>
            NSO Group. (2021, July 21). Enough is enough.
            https://www.nsogroup.com/Newses/enough-is-enough/.
            <br></br>
            <br></br>
            NSO Group. (n.d.). Governance
            https://www.nsogroup.com/Newses/enough-is-enough/.
            <br></br>
            <br></br>
            Olivas Osuna, J. J. (2023). The Pegasus spyware scandal: a critical
            review of Citizen Lab's" CatalanGate".
            https://www.researchgate.net/publication/369186976_The_Pegasus_spyware_scandal
            _A_critical_review_of_Citizen_Lab's_CatalanGate_report
            <br></br>
            <br></br>
            O’Neill, P. H. (2020). The Man Who Built a Spyware Empire Says It’s
            Time to Come Out of the Shadows’.
            https://www.technologyreview.com/2020/08/19/1007337/shalev-hulio-
            nso-group-spyware-interview/
            <br></br>
            <br></br>
            Ouagal, M., Hendrikx, P., Saegerman, C., & Berkvens, D. (2010).
            Comparison between active and passive surveillance within the
            network of epidemiological surveillance of animal diseases in Chad.
            Acta Tropica, 116(2), 147-151.
            https://doi.org/10.1016/j.actatropica.2010.07.004
            <br></br>
            <br></br>
            Richard, L., & Rigaud, S. (2021) The Pegasus Project: A Worldwide
            Collaboration to Counter a Global Crime. Forbiddenstories.org.
            https://forbiddenstories.org/the-pegasus-
            project-a-worldwide-collaboration-to-counter-a-global-crime/
            <br></br>
            <br></br>
            Suchman, M. C. (1995). Managing legitimacy: Strategic and
            institutional approaches. The Academy of Management Review, 20(3),
            571-610. https://doi.org/10.2307/258788
            <br></br>
            <br></br>
            Van Dijck, J. (2014). Datafication, dataism and dataveillance: Big
            data between scientific paradigm and ideology. Surveillance &
            Society, 12(2), 197-208. https://doi.org/10.24908/ss.v12i2.4776
            <br></br>
            <br></br>
            Wahl-Jorgensen, K., Bennett, L., & Taylor, G. (2017). The
            normalization of surveillance and the invisibility of digital
            citizenship: Media debates after the Snowden revelations.
            International Journal of Communication (Online), , 740.
            <br></br>
            <br></br>
            Zuboff, S. (2019). Surveillance capitalism and the challenge of
            collective action. New Labor Forum, 28(1), 10-29.
            https://doi.org/10.1177/1095796018819461
          </p>
        </div>
      </section>
    );
  }
}

export default Pegasus;
